import * as React from "react";
import { useMappedState } from "redux-react-hook";
import styled, { css } from "styled-components";
import * as H from "history";

import Grid from "src/library/components/Hedron";
import { BrandSplitView } from "src/library/components/BrandSplitView";
import { Text } from "src/library/components/Text";
import PoweredByMezze from "src/library/components/PoweredByMezze/PoweredByMezze";
import { Logo } from "src/core/shared/components/Logo";
import type { IStore } from "src/core/shared/store";
import { PageProps } from "src/pages/Router/routes";
import { Button } from "src/library/components/Button";
import { useAuth } from "src/core/shared/api/auth";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";

/**
 * Main "Login" View
 *
 * @param {*} { user, getUserByPayroll, history }
 * @returns
 */
const CompletingSocialLoginPage: React.ReactFCWithChildren<PageProps<unknown>> = props => {
  const { currentUserStatus, globalPlatformSettings } = useMappedState(({ global }: IStore) => ({
    currentUserStatus: global.userSuspended,
    globalPlatformSettings: global.settings && global.settings.platform,
    globalPointOfContact: global.settings && global.settings.pointOfContact
  }));

  const [ , { error } ] = useAuth(true, props.history as H.History<{from?: string}>, true);

  return (
    <BrandSplitView currentUserStatus={currentUserStatus}>
      <AuthInner direction="vertical">
        <Grid.Box className="logo-wrapper">
          <StyledDarkLogo />
        </Grid.Box>

        <Grid.Box className="loading-section">
          <ErrorNotice
            message={error}
            type="error"
          />

          {!error && (
            <>
              <LoadingSpinner
                className="loading-spinner"
                type="circular-pulse"
                position="inline"
              />

              <Text
                fontSize="h4"
                color="secondary"
              >
                Logging you in, please wait....
              </Text>
            </>
          )}
        </Grid.Box>

        {globalPlatformSettings && globalPlatformSettings.maintenance_mode.roles.length > 0 && (
          <Grid.Box style={{ paddingTop: "14px" }}>
            <Text
              fontSize="h4"
              color="secondary"
            >
              System in maintenance mode
            </Text>
          </Grid.Box>
        )}

        <div>
          <Button
            onClick={() => {
              window.location.href = "/";
            }}
          >
            Go Back
          </Button>
        </div>

        <AuthFooter direction="vertical">
          <PoweredByMezze type="light" />
        </AuthFooter>
      </AuthInner>
    </BrandSplitView>
  );
};

const StyledDarkLogo = styled(Logo).attrs({ type: "dark" })`
  margin-bottom: 30px;
`;

export const AuthFormWrapper = styled(Grid.Box)`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing.margins.xl};

  form {
    width: 100%;
    max-width: 400px;
  }
`;

export const AuthFooter = styled(Grid.Bounds)`
  margin-top: ${({ theme }) => theme.spacing.margins.lg};
  width: 100%;
  align-items: center;
`;

export const AuthInner = styled(Grid.Bounds)`
  ${({ theme }) =>
    css`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: ${theme.spacing.padding.xl};

    .logo-wrapper {
      img {
        max-width: 300px;
        max-height: 120px;
      }
    }
    
    .loading-section .loading-spinner {
      margin-top: ${theme.spacing.margins.md};
    }

    button {
      margin-top: ${theme.spacing.margins.md};
    } 
  `};
`;

export default CompletingSocialLoginPage;
